import { absoluteValue } from '@0xtorch/big-decimal'
import type { Action } from '@0xtorch/core'
import {
  ApiSecretRequiredException,
  AssetNotFoundException,
  SubTypeRequiredException,
} from '../../errors'
import type { FetchApiData } from '../../types'
import { createGetAsset } from '../../utils/createGetAsset'
import { gateAccountCategory } from '../constants'
import { createClient } from '../createClient'
import { responseSchema } from './schema'

export const fetchApiData: FetchApiData = async ({
  apiKey,
  apiSecret,
  assets,
  config,
  from,
  createHeaders,
  proxy,
  subType,
  symbolAssetMap,
  to,
}) => {
  if (!apiSecret) {
    throw new ApiSecretRequiredException()
  }
  if (!subType) {
    throw new SubTypeRequiredException()
  }

  const getAsset = createGetAsset({ assets, symbolAssetMap })
  const asset = getAsset(subType)
  if (!asset) {
    throw new AssetNotFoundException(subType)
  }

  const client = createClient({
    apiKey,
    apiSecret,
    createHeaders,
    proxy,
  })

  const response = await client.run({
    method: 'GET',
    path: `/futures/${subType}/account_book`,
    query: {
      limit: config.limit.toString(),
      ...(from ? { from: Math.floor(from / 1000).toString() } : {}), // to seconds
      ...(to ? { to: Math.ceil(to / 1000).toString() } : {}), // to seconds
    },
    responseSchema,
  })

  // time で降順ソート (time は秒単位)
  const sortedItems = response.sort((a, b) => b.time - a.time)

  let fetchedFrom: number | undefined
  let fetchedTo: number | undefined
  const actions: Action[] = []

  for (const item of sortedItems) {
    const timestamp = item.time * 1000 // ミリ秒に変換

    // fetchedFrom / fetchedTo 更新
    fetchedFrom =
      fetchedFrom === undefined ? timestamp : Math.min(fetchedFrom, timestamp)
    fetchedTo =
      fetchedTo === undefined ? timestamp : Math.max(fetchedTo, timestamp)

    // type 毎に処理
    switch (item.type) {
      case 'bonus_dnw':
      case 'dnw': {
        // Deposit & Withdraw はスキップ
        continue
      }
      case 'pnl': {
        if (item.change.value === 0n) {
          continue
        }
        actions.push({
          comment: `Close Position > ${item.text}`,
          evidence: 'system-rule',
          order: 0,
          source: `${gateAccountCategory}_${config.type}_${item.id ?? timestamp.toString()}`,
          type: item.change.value > 0n ? 'realize-profit' : 'realize-loss',
          timestamp,
          transfers: [
            {
              asset,
              amount: absoluteValue(item.change),
              direction: item.change.value > 0n ? 'in' : 'out',
            },
          ],
        })
        continue
      }
      case 'fund': {
        if (item.change.value === 0n) {
          continue
        }
        actions.push({
          comment: `Funding > ${item.text}`,
          evidence: 'system-rule',
          order: 0,
          source: `${gateAccountCategory}_${config.type}_${item.id ?? timestamp.toString()}`,
          type: item.change.value > 0n ? 'derivative-profit' : 'derivative-fee',
          timestamp,
          transfers: [
            {
              asset,
              amount: absoluteValue(item.change),
              direction: item.change.value > 0n ? 'in' : 'out',
            },
          ],
        })
        continue
      }
      case 'fee': {
        if (item.change.value === 0n) {
          continue
        }
        if (item.change.value > 0n) {
          throw new Error('fee type with positive change is unsupported')
        }
        actions.push({
          comment: `Trading Fee > ${item.text}`,
          evidence: 'system-rule',
          order: 0,
          source: `${gateAccountCategory}_${config.type}_${item.id ?? timestamp.toString()}`,
          type: 'fee',
          timestamp,
          transfers: [
            {
              asset,
              amount: absoluteValue(item.change),
              direction: 'out',
            },
          ],
        })
        continue
      }
      case 'refr': {
        if (item.change.value === 0n) {
          continue
        }
        if (item.change.value < 0n) {
          throw new Error('refr type with negative change is unsupported')
        }
        actions.push({
          comment: `Referrer Rebate > ${item.text}`,
          evidence: 'system-rule',
          order: 0,
          source: `${gateAccountCategory}_${config.type}_${item.id ?? timestamp.toString()}`,
          type: 'gift',
          timestamp,
          transfers: [
            {
              asset,
              amount: item.change,
              direction: 'in',
            },
          ],
        })
        continue
      }
      default: {
        // 未対応 type
        throw new Error(`Unsupported futures change history type: ${item.type}`)
      }
    }
  }

  // timestamp 降順ソート
  const sortedActions = actions.sort((a, b) => b.timestamp - a.timestamp)

  return {
    actions: sortedActions,
    count: response.length,
    from: fetchedFrom,
    sources: [],
    stopped: false,
    to: fetchedTo,
  }
}
